import type React from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Globe, Search } from 'lucide-react'
import DropdownChevron from './dropdown-chevron'

type CountryOption = {
    value?: string
    label: string
}

interface PhoneCountrySelectProps {
    name?: string
    value?: string
    onChange: (value?: string) => void
    onFocus?: () => void
    onBlur?: () => void
    options: CountryOption[]
    iconComponent: React.ElementType
    disabled?: boolean
    readOnly?: boolean
    tabIndex?: number | string
    className: string
}

const PhoneCountrySelect: React.FC<PhoneCountrySelectProps> = ({
    name,
    value,
    onChange,
    onFocus,
    onBlur,
    options,
    iconComponent: IconComponent,
    disabled,
    readOnly,
    tabIndex,
    className: _className,
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const rootRef = useRef<HTMLDivElement | null>(null)
    const searchInputRef = useRef<HTMLInputElement | null>(null)

    const selectedOption = useMemo(
        () => options.find((option) => option.value === value),
        [options, value]
    )

    const filteredOptions = useMemo(() => {
        const query = searchQuery.trim().toLowerCase()
        if (!query) return options

        return options.filter((option) => option.label.toLowerCase().includes(query))
    }, [options, searchQuery])

    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
                setIsOpen(false)
                setSearchQuery('')
            }
        }

        document.addEventListener('mousedown', handleOutsideClick)
        return () => document.removeEventListener('mousedown', handleOutsideClick)
    }, [])

    useEffect(() => {
        if (!isOpen) return

        const frameId = window.requestAnimationFrame(() => {
            searchInputRef.current?.focus()
        })

        return () => window.cancelAnimationFrame(frameId)
    }, [isOpen])

    const handleSelect = (nextValue?: string) => {
        onChange(nextValue)
        setIsOpen(false)
        setSearchQuery('')
    }

    const handleToggle = () => {
        if (disabled || readOnly) return

        setIsOpen((prev) => {
            if (prev) {
                setSearchQuery('')
            }
            return !prev
        })
    }

    const normalizedTabIndex =
        typeof tabIndex === 'number'
            ? tabIndex
            : typeof tabIndex === 'string'
                ? Number(tabIndex)
                : undefined

    return (
        <div ref={rootRef} className="phone-country-select">
            <button
                type="button"
                name={name}
                tabIndex={Number.isNaN(normalizedTabIndex) ? undefined : normalizedTabIndex}
                disabled={disabled}
                onFocus={onFocus}
                onBlur={onBlur}
                onMouseDown={(event) => {
                    event.preventDefault()
                    handleToggle()
                }}
                className="phone-country-select-trigger"
                aria-label={selectedOption ? `Country: ${selectedOption.label}` : 'Select country'}
            >
                {selectedOption ? (
                    <IconComponent country={selectedOption.value} label={selectedOption.label} />
                ) : (
                    <Globe className="h-5 w-5 text-gray-500" aria-hidden="true" />
                )}
                <DropdownChevron isOpen={isOpen} disabled={disabled || readOnly} />
            </button>

            {isOpen && !disabled && !readOnly && (
                <div className="phone-country-select-menu">
                    <div className="phone-country-select-search">
                        <Search className="phone-country-select-search-icon" aria-hidden="true" />
                        <input
                            ref={searchInputRef}
                            type="text"
                            value={searchQuery}
                            onChange={(event) => setSearchQuery(event.target.value)}
                            onMouseDown={(event) => event.stopPropagation()}
                            onKeyDown={(event) => {
                                if (event.key === 'Escape') {
                                    setIsOpen(false)
                                    setSearchQuery('')
                                }
                            }}
                            placeholder="Search country"
                            className="phone-country-select-search-input"
                            aria-label="Search country by name"
                        />
                    </div>

                    <div className="phone-country-select-options">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option) => (
                                <button
                                    type="button"
                                    key={option.value ?? 'ZZ'}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => handleSelect(option.value)}
                                    className="phone-country-select-option"
                                >
                                    {option.label}
                                </button>
                            ))
                        ) : (
                            <p className="phone-country-select-empty">No countries found</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

export default PhoneCountrySelect
